import type { IBufferLine, IDisposable, ILink, Terminal } from '@xterm/xterm';

/**
 * Clickable URLs in a PTY terminal, including ones a program broke over
 * several rows (FRONT-3172).
 *
 * Claude Code's sign-in screen prints a ~420-character URL and its renderer
 * HARD-wraps it at the terminal width: real line breaks, not xterm's soft
 * wrap. xterm therefore sees three unrelated rows, so selecting the URL
 * copies line breaks into it and nothing is clickable. This provider treats
 * a row that runs to the right edge, ending in a URL character, followed by a
 * row that starts at the same indent with a URL character, as ONE line, finds
 * http(s) URLs in the joined text, and opens the whole URL on click.
 *
 * The join is a heuristic: a URL that happens to end exactly at the right
 * edge, followed by a row starting with a word at the same indent, is glued
 * to that word. Joining stops at the first blank, so at most one word is.
 */

/** One terminal row: the text of each cell by column (null = second half of a wide character), and xterm's own soft-wrap flag. */
export interface LinkRow {
  cells: (string | null)[];
  /** True when xterm soft-wrapped the PREVIOUS row into this one. */
  isWrapped: boolean;
}

/** A found link: its full text and inclusive 1-based cell range, in xterm's coordinates (y = buffer line number). */
export interface StitchedLink {
  url: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
}

/** How many rows above and below the asked row a link may span — far above a sign-in URL on a narrow terminal. */
export const MAX_STITCH_ROWS = 24;

// RFC 3986 characters minus the quote, so box-drawing rules, quotes and prose
// punctuation outside ASCII never join a URL.
const URL_PATTERN = /https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&()*+,;=%]+/g;
const URL_CHAR = /^[A-Za-z0-9\-._~:/?#[\]@!$&()*+,;=%]$/;
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

const isBlank = (ch: string | null | undefined) => ch === null || ch === undefined || ch === '' || ch === ' ';

function leadingBlanks(row: LinkRow): number {
  let x = 0;
  while (x < row.cells.length && isBlank(row.cells[x])) x++;
  return x;
}

function lastFilled(row: LinkRow): number {
  let x = row.cells.length - 1;
  while (x >= 0 && isBlank(row.cells[x])) x--;
  return x;
}

/**
 * Whether row `y` carries on into row `y + 1`: xterm soft-wrapped it, or the
 * program hard-wrapped it — `y` is filled to the right edge (one cell of
 * slack, mirrored by the indent for a padded box) and ends in a URL
 * character, and `y + 1` starts at the same indent with one.
 */
export function rowContinues(rows: readonly LinkRow[], y: number, cols: number, indent: number): boolean {
  const row = rows[y];
  const next = rows[y + 1];
  if (!row || !next) return false;
  if (next.isWrapped) return true;
  const last = lastFilled(row);
  if (last < cols - 2 - indent) return false;
  if (!URL_CHAR.test(row.cells[last] ?? '')) return false;
  if (leadingBlanks(next) !== indent) return false;
  return URL_CHAR.test(next.cells[indent] ?? '');
}

/** Trim sentence punctuation and an unbalanced closing bracket off the end of a matched URL. */
export function trimUrl(raw: string): string {
  let url = raw;
  for (;;) {
    const before = url;
    url = url.replace(TRAILING_PUNCTUATION, '');
    const tail = url.at(-1);
    if (tail && CLOSERS[tail]) {
      const opens = url.split(CLOSERS[tail]).length - 1;
      const closes = url.split(tail).length - 1;
      if (closes > opens) url = url.slice(0, -1);
    }
    if (url === before) return url;
  }
}

/**
 * The http(s) links touching row `y` (0-based index into `rows`), each
 * stitched across every row it continues over. `lineOffset` turns a row
 * index into xterm's 1-based buffer line number.
 */
export function findStitchedLinks(rows: readonly LinkRow[], y: number, cols: number, lineOffset = 1): StitchedLink[] {
  if (!rows[y]) return [];
  // Walk up to the start of the logical line. The indent is the start row's
  // own, so it is re-derived for each candidate start.
  let top = y;
  while (top > 0 && y - top < MAX_STITCH_ROWS && rowContinues(rows, top - 1, cols, leadingBlanks(rows[top - 1]!))) top--;
  const indent = leadingBlanks(rows[top]!);
  let bottom = y;
  while (bottom - y < MAX_STITCH_ROWS && rowContinues(rows, bottom, cols, indent)) bottom++;

  // Join the rows, remembering which cell every character came from.
  let text = '';
  const origin: { x: number; y: number }[] = [];
  for (let r = top; r <= bottom; r++) {
    const row = rows[r]!;
    const hardJoin = r > top && !row.isWrapped;
    const from = hardJoin ? indent : 0;
    const nextHard = r < bottom && !rows[r + 1]!.isWrapped;
    const to = nextHard ? lastFilled(row) : row.cells.length - 1;
    for (let x = from; x <= to; x++) {
      const ch = row.cells[x];
      if (ch === null || ch === undefined) continue;
      const chars = ch === '' ? ' ' : ch;
      for (let i = 0; i < chars.length; i++) {
        text += chars[i];
        origin.push({ x, y: r });
      }
    }
  }

  const links: StitchedLink[] = [];
  for (const match of text.matchAll(URL_PATTERN)) {
    const url = trimUrl(match[0]);
    if (url.length <= 'https://'.length) continue;
    const first = origin[match.index]!;
    const last = origin[match.index + url.length - 1]!;
    if (last.y < y || first.y > y) continue;
    links.push({
      url,
      start: { x: first.x + 1, y: first.y + lineOffset },
      end: { x: last.x + 1, y: last.y + lineOffset },
    });
  }
  return links;
}

/** Open `url` in a new tab, only for http(s) and never with a handle back to Eddy. */
export function openTerminalLink(url: string, open: typeof window.open = (...args) => window.open(...args)): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
  open(parsed.href, '_blank', 'noopener,noreferrer');
}

function toLinkRow(line: IBufferLine | undefined, cols: number): LinkRow {
  const cells: (string | null)[] = [];
  if (!line) return { cells, isWrapped: false };
  for (let x = 0; x < cols; x++) {
    const cell = line.getCell(x);
    if (!cell) cells.push('');
    else if (cell.getWidth() === 0) cells.push(null);
    else cells.push(cell.getChars());
  }
  return { cells, isWrapped: line.isWrapped };
}

/** Register the stitched-link provider on `term`; disposed with the terminal or by the returned handle. */
export function installStitchedLinks(term: Pick<Terminal, 'registerLinkProvider' | 'buffer' | 'cols'>, open: (url: string) => void = openTerminalLink): IDisposable {
  return term.registerLinkProvider({
    provideLinks(lineNumber, callback) {
      const buffer = term.buffer.active;
      const cols = term.cols;
      const y = lineNumber - 1;
      const first = Math.max(0, y - MAX_STITCH_ROWS);
      const last = Math.min(buffer.length - 1, y + MAX_STITCH_ROWS);
      const rows: LinkRow[] = [];
      for (let i = first; i <= last; i++) rows.push(toLinkRow(buffer.getLine(i), cols));
      const found = findStitchedLinks(rows, y - first, cols, first + 1);
      const links: ILink[] = found.map((link) => ({
        text: link.url,
        range: { start: link.start, end: link.end },
        decorations: { underline: true, pointerCursor: true },
        activate: () => open(link.url),
      }));
      callback(links.length ? links : undefined);
    },
  });
}
